import { styled, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { Typography } from '../../../components/Typography/Typography';
import { WorkflowStagesStrip } from '../../../components/WorkflowStages/WorkflowStagesStrip';
import { Workflow } from '../../../types';

type Props = {
  workflow: Workflow;
};

// Whole panel cross-fades / slides in when the selected workflow changes.
const panelVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 320, damping: 30 } },
};

/**
 * Right-hand detail pane for the Workflows page: the selected workflow's name, its approval pipeline
 * (Submitter -> stages -> Closed, on a single row) and its non-working (default, non-deal) rows.
 * Purely presentational and read-only.
 */
export function WorkflowDetail({ workflow }: Props) {
  const { colors } = useTheme();
  const stages = workflow.approvalStages ?? [];
  const rows = workflow.defaultRows ?? [];

  return (
    <Panel
      key={workflow.id}
      variants={panelVariants}
      initial='hidden'
      animate='visible'
      style={{
        backgroundColor: colors.surfaceBackground.bg1,
        border: `1px solid ${colors.border.default}`,
      }}
    >
      <Typography variant='h2' color={colors.text.main} style={{ marginBottom: 0 }}>
        {workflow.name}
      </Typography>

      <Section>
        <SectionLabel variant='overline' color={colors.text.caption} style={{ marginBottom: 0 }}>
          Approval pipeline
        </SectionLabel>
        <WorkflowStagesStrip
          stages={stages}
          size='large'
          animationKey={workflow.id}
          labels={workflow.stageLabels}
        />
      </Section>

      <Divider style={{ backgroundColor: colors.border.default }} />

      <Section>
        <SectionLabel variant='overline' color={colors.text.caption} style={{ marginBottom: 0 }}>
          Non-working rows
        </SectionLabel>
        {rows.length ? (
          <RowChips>
            {rows.map((row) => (
              <RowChip
                key={row}
                style={{
                  backgroundColor: colors.surfaceBackground.bg2,
                  border: `1px solid ${colors.border.default}`,
                }}
              >
                <Typography variant='body' color={colors.text.secondary}>
                  {row}
                </Typography>
              </RowChip>
            ))}
          </RowChips>
        ) : (
          <Typography variant='body' color={colors.text.caption}>
            No non-working rows
          </Typography>
        )}
      </Section>
    </Panel>
  );
}

const Panel = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 36px;
  border-radius: 14px;
  min-height: 260px;
`;

const Section = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionLabel = styled(Typography)`
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const Divider = styled('div')`
  height: 1px;
  width: 100%;
`;

const RowChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const RowChip = styled('div')`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 10px;
  white-space: nowrap;
`;
